import React from 'react';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import {
  calculateEnhancedTeamStructure,
  PROJECT_TEAM_LOCATION_LABELS,
  PROJECT_TEAM_TYPE_LABELS,
} from 'domain/shared/newProject';
import { useShowContext } from 'framework/show';
import { StaffingDataType } from 'domain/shared/staffing';
import { EnumField, FormattedTextField, TextField } from 'framework/ui/field';
import { AppIconLabel } from 'framework/ui/mui-builder';
import { DATE_FORMAT, useDateService, formatDecimal, EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { IconSectionRow } from 'framework/ui/layout';

const IconLabel = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', width: 40, minWidth: 40, flexShrink: 0 }}>
    {children}
  </Box>
);

const StaffingProjectGeneralInfo = () => {
  const { data } = useShowContext<StaffingDataType>();
  const { team, ...projectData } = data.getData() || {};
  const { formatDate } = useDateService();

  const {
    allFte = 0,
    assignedFte = 0,
    isNeedCreateTeam,
  } = calculateEnhancedTeamStructure(team, projectData?.budget?.fte, projectData?.budget?.billingType);

  const isAvailable = allFte != null && allFte > 0 ? true : false;
  const percentage = allFte > 0 ? Math.round((assignedFte / allFte) * 100) : assignedFte > 0 ? 100 : 0;
  const difference = isAvailable ? allFte - assignedFte : 0;

  if (isNeedCreateTeam) {
    return (
      <Box>
        <Typography variant="body1" color="textSecondary" fontStyle="italic">
          The team for this project has not been created.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} sx={{ pb: 2 }}>
      <Grid size={12}>
        <IconSectionRow colXl={6} colMd={6} sxItem={{ mb: '12px!important' }}>
          <TextField
            value={
              isAvailable
                ? `${formatDecimal(assignedFte)}/${formatDecimal(allFte)} FTE (${formatDecimal(percentage)}%)`
                : '—'
            }
            label={
              <IconLabel>
                <AppIconLabel icon="group" tooltipTitle="Assigned/Required FTE" />
              </IconLabel>
            }
            mb={2}
          />
          <TextField
            value={isAvailable ? `${formatDecimal(difference)} FTE` : '—'}
            label={
              <IconLabel>
                <AppIconLabel icon="peoplePlus" tooltipTitle="Difference FTE" />
              </IconLabel>
            }
          />

          <EnumField
            source="location"
            value={team?.location}
            label={
              <IconLabel>
                <AppIconLabel icon="location" tooltipTitle="Team location" />
              </IconLabel>
            }
            options={PROJECT_TEAM_LOCATION_LABELS}
          />

          <EnumField
            source="type"
            value={team?.type}
            label={
              <IconLabel>
                <AppIconLabel icon="cubes" tooltipTitle="Type" />
              </IconLabel>
            }
            options={PROJECT_TEAM_TYPE_LABELS}
          />

          <FormattedTextField
            source="type"
            value={
              projectData.budget
                ? `${formatDate(projectData?.budget?.startDate, DATE_FORMAT.FULL_DATE) || EMPTY_VALUE_INDICATOR} - ${formatDate(projectData?.budget?.endDate, DATE_FORMAT.FULL_DATE) || EMPTY_VALUE_INDICATOR}`
                : EMPTY_VALUE_INDICATOR
            }
            label={
              <IconLabel>
                <AppIconLabel icon="outlinedCalendar" tooltipTitle="Project duration" />
              </IconLabel>
            }
            options={PROJECT_TEAM_TYPE_LABELS}
          />
        </IconSectionRow>
      </Grid>
    </Grid>
  );
};

export default StaffingProjectGeneralInfo;
